<template>
  <div>
    <question-usage
      v-if="$page.props.user.current_team.plan !== 'Enterprise'"
      :key="key"
      :faq-id="faqId"
      :show-based-on-used-percentage="true"
      class="mb-4"
    />
    <v-form @submit.prevent="submit">
      <v-card>
        <v-card-title>
          <span v-if="mode === 'edit'">
            {{ $t('Edit question') }}
          </span>
          <span v-else-if="mode === 'create'">
            {{ $t('Create a question') }}
          </span>
        </v-card-title>
        
        <v-card-text>
          <div
            v-if="mode === 'edit'"
          >
            <div class="mb-2">
              <span class="font-weight-medium">{{ $t('Created') }}: </span>
              {{ question.created }}
              <div
                v-if="question.created_by"
                class="d-inline-block"
              >
                {{ $t('by') }} {{ question.created_by }}
              </div>
            </div>
            <div v-if="question.created_at != question.last_modified_at">
              <span class="font-weight-medium">{{ $t('Updated') }}: </span>
              {{ question.last_modified }}
              <div
                v-if="question.last_modified_by"
                class="d-inline-block"
              >
                {{ $t('by') }} {{ question.last_modified_by }}
              </div>
            </div>
          </div>

          <div
            v-if="mode === 'edit'"
            class="mb-2 body-2"
          >
            <v-row class="align-center">
              <v-col cols="auto">
                <v-switch
                  v-model="form.published"
                />
              </v-col>

              <v-col cols="auto">
                <span v-if="!form.published">
                  <v-icon color="warning">mdi-eye-off-outline</v-icon>  
                  {{ $t('Question is unpublished (hidden)') }}
                </span>
    
                <span v-else>
                  <v-icon color="success">mdi-eye-outline</v-icon>  
                  {{ $t('Question is published (visible)') }}
                </span>
              </v-col>
            </v-row>
          </div>
          
          <div
            v-else
            class="mb-2 body-2"
          >
            <v-row class="align-center">
              <v-col cols="auto">
                <v-switch
                  v-model="form.published"
                />
              </v-col>

              <v-col cols="auto">
                <span v-if="!form.published">
                  <v-icon color="warning">mdi-eye-off-outline</v-icon>  
                  {{ $t('Question will be unpublished (hidden)') }}
                </span>
    
                <span v-else>
                  <v-icon color="success">mdi-eye-outline</v-icon>  
                  {{ $t('Question will be published (visible)') }}
                </span>
              </v-col>
            </v-row>
          </div>          

          <v-expansion-panels
            v-model="categoriesExpanded"
            class="mb-8"
          >
            <v-expansion-panel>
              <v-expansion-panel-header>{{ $t('Categories') }}</v-expansion-panel-header>
              <v-expansion-panel-content
                class="pt-1"
                eager
              >
                <v-row
                  :no-gutters="$vuetify.breakpoint.xsOnly"
                  class="mb-0"
                >
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <select-or-create-category
                      ref="categorySelect"
                      v-model="form.category"
                      :preserve-state="true"
                      :class="{'mb-6': $vuetify.breakpoint.xsOnly}"
                      :disabled="form.processing"
                      :loading="form.processing"
                      :items="categories"
                      :faq-id="faqId"
                      :error-messages="errors.category"
                      hide-details="auto"
                      clearable
                      item-text="default_translation.name"
                      item-value="id"
                      :no-data-text="$t('No categories created')"
                      outlined
                      :return-object="false"
                      :label="$t('Select or create category')"
                      :create-option-text="$t('Create new category')"
                      :dialog-title="$t('Create new category')"
                      @input="handleCategoryInput"
                      @category-created="handleCategoryCreated"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <select-or-create-category
                      ref="subcategorySelect"
                      v-model="form.subcategory"
                      :category-id="form.category"
                      :preserve-state="true"
                      :class="{'mb-2': $vuetify.breakpoint.xsOnly}"
                      :is-subcategory="true"
                      :disabled="! form.category || form.processing"
                      :loading="form.processing"
                      :items="filteredSubcategories"
                      :faq-id="faqId"
                      hide-details="auto"
                      clearable
                      item-text="default_translation.name"
                      item-value="id"
                      :no-data-text="$t('No subcategories created')"
                      outlined
                      :return-object="false"
                      :label="$t('Select or create subcategory')"
                      :create-option-text="$t('Create new subcategory')"
                      :dialog-title="$t('Create new subcategory')"
                      @subcategory-created="handleSubcategoryCreated"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-text-field
            ref="questionTitleInput"
            v-model="form.question_title"
            autofocus
            :class="{
              'mb-6': ! errors.question_title
            }"
            hide-details="auto"
            :disabled="form.processing || openAiLoading"
            :loading="form.processing"
            :error-messages="errors.question_title"
            :label="$t('Enter question title')"
            outlined
          />

          <open-ai-expansion-panel
            v-if="mode === 'create'"
            class="mb-6"
          >
            <p
              v-if="! openAiDescription"
              class="mt-2 mb-0"
            >
              {{ $t('You must specify an OpenAI description under "Settings > General" to use this feature') }}
            </p>

            <div v-else>
              <p class="mb-4">
                {{ $t('Click the button below to generate an example answer using OpenAI') }}
              </p>
              <v-btn
                :disabled="! openAiDescription || form.question_title.length < 5 || openAiLoading"
                :loading="openAiLoading"
                color="primary"
                @click="generateAnswerUsingOpenAi"
              >
                {{ $t('Generate answer') }}
              </v-btn>
            </div>
          </open-ai-expansion-panel>

          <wysiwyg-editor
            :key="$page.props.app_locales.current.code"
            v-model="form.answer_body"
            :placeholder="$t('Enter an answer')"
            :error-messages="errors.answer_body"
            :disabled="form.processing || openAiLoading"
            @onClick="handleEditorOnClick"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            type="submit"
            :loading="form.processing"
            :disabled="form.processing"
            color="primary"
          >
            <template v-if="mode === 'create'">
              {{ $t('Create question') }}
            </template>
            <template v-else-if="mode === 'edit'">
              {{ $t('Save changes') }}
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import axios from 'axios';
import { router } from '@inertiajs/vue2';
import maxBy from 'lodash/maxBy';
import WysiwygEditor from '@/components/shared/WysiwygEditor';
import OpenAiExpansionPanel from '@/components/shared/OpenAiExpansionPanel';

export default {
  components: {
    WysiwygEditor,
    OpenAiExpansionPanel
  },

  props: {
    faqId: {
      type: Number,
      required: true
    },
    question: {
      type: Object,
      required: false,
      default: null
    },
    categories: {
      type: Array,
      required: true
    },
    subcategories: {
      type: Array,
      required: true
    },
    mode: {
      type: String,
      required: true,
      validator: function(value) {
        return ['create', 'edit'].indexOf(value) !== -1;
      }
    },
    categoryId: {
      type: Number,
      required: false,
      default: null
    },
    subcategoryId: {
      type: Number,
      required: false,
      default: null
    },
    errors: {
      type: Object,
      required: false,
      default: () => ({})
    },
    openAiDescription: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      key: 0,
      categoriesExpanded: null,
      form: this.$inertia.form({
        language_code: null,
        question_title: '',
        answer_body: '',
        category: null,
        subcategory: null,
        published: true
      }),
      openAiLoading: false,
    }
  },

  computed: {
    filteredSubcategories() {
      return this.subcategories.filter(subcategory => subcategory.category_id === this.form.category);
    },
  },

  beforeDestroy() {
    router.remember(this.form.category, 'category');
    router.remember(this.form.subcategory, 'subcategory');
  },

  mounted() {
    if (this.mode === 'create') {
      this.restoreSelectedCategories();
    }
  },

  created() {
    this.$root.preview.faq.component = 'embed';

    this.setInitialValues();
  },

  methods: {
    setInitialValues() {
      if (this.mode === 'edit') {
        this.form.category = this.question.category_id;
        this.form.subcategory = this.question.subcategory_id;
        this.form.language_code = this.question.default_translation.language_code;
        this.form.question_title = this.question.default_translation.title;
        this.form.answer_body = this.question.default_translation.answer;
        this.form.published = this.question.published;

        if (this.form.category) {
          const category = this.categories.find(
            cat => cat.id === this.form.category
          );
          if (! category.uncategorized) {
            this.categoriesExpanded = 0;
          }
        }
      } else if (this.mode === 'create') {
        this.form.category = this.categoryId;
        this.form.subcategory = this.subcategoryId;

        if (this.categoryId || this.subcategoryId) {
          this.categoriesExpanded = 0;
        }
      }
    },

    restoreSelectedCategories() {
      const category = router.restore('category');
      const subcategory = router.restore('subcategory');

      if (category) {
        this.form.category = category;
        this.categoriesExpanded = 0;
      }

      if (subcategory) {
        this.form.subcategory = subcategory;
        this.categoriesExpanded = 0;
      }
    },

    handleLanguageCodeChange(code) {
        if (code === this.question.default_translation.language.code) {
          this.setInitialValues();
          return;
        }

        const translation = this.question.translations.find(t => t.language.code === code);
        this.form.question_title = translation.title;
        this.form.answer_body = translation.answer;
    },

    handleEditorOnClick() {
      this.$refs.categorySelect.blur();
      this.$refs.subcategorySelect.blur();
    },

    handleCategoryCreated() {
      const createdCategory = maxBy(this.categories, category => category.id);
      this.form.category = createdCategory.id;
    },

    handleSubcategoryCreated() {
      const createdSubcategory = maxBy(this.subcategories, subcategory => subcategory.id);
      this.form.subcategory = createdSubcategory.id;
    },

    submit() {
      if (this.mode === 'create') {
        this.form.post(this.route('faq.question.store', this.faqId), {
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            this.key++;
            this.form.question_title = '';
            this.form.answer_body = '';

            this.$root.refreshFaqPreview();
          }
        });
      } else {
        this.form.patch(this.route('faq.question.update', [this.faqId, this.question.id]), {
          onSuccess: () => {
            this.setInitialValues();

            this.$root.refreshFaqPreview();
          }
        });
      }
    },

    handleCategoryInput() {
      if (this.form.subcategory !== null) {
        this.$nextTick(() => {
          this.form.subcategory = null;
        });
      }
    },

    async generateAnswerUsingOpenAi() {
      const url = this.route('faq.open-ai.generate-answer', [this.faqId]);

      try {
        this.openAiLoading = true;

        const response = await axios.post(url, {
          question: this.form.question_title,
          category: this.form.category,
          subcategory: this.form.subcategory
        });

        this.openAiLoading = false;

        this.form.answer_body = response.data.answer;
      } catch (err) {
        this.openAiLoading = false;
        this.$root.$snack('error', this.$t('Something went wrong'), 1500);
      }
    }
  }
}
</script>